import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PackagingProductProps, ProductProps } from '@/types';
import { useCalcPrice, useDbFunctions } from '@/talons';
import { OrderPayloadProps } from './type';
import { useNavigate } from 'react-router-dom';
import { CartItems } from '@/hooks/CartContext';
import { StockProps } from '@/hooks/StockContext';
import { useLoader } from '@/hooks/LoaderContext';
import cartItemsMock from '@/mocks/cartItemsMocks';
import {
  setOnStorage,
  getOnStorage,
  normalizeParametroPrecoBase,
  findDirectPriceRecord,
  isNroSegmentoPrecoCuringa,
} from '@/shared';
import { useCallback, useMemo, useState } from 'react';
import { HandlePrices } from '@/shared/calculatePrices';
import shippingMethod from '@/mocks/shippingMethodMocks';
import { useEvents } from '../use-events/useEvents';

import {
  useCart,
  useStock,
  useClient,
  useGlobals,
  useFetchAxios,
  useCartTotals,
  useUserContext,
  useDiscountFlex,
  useDiscountAmountFlex,
  useNetworkStatusContext,
  useTables,
  usePrices,
} from '@/hooks';

interface CartProductItem {
  item: CartItems;
  product: ProductProps | undefined;
}

type QaConfigCheckout = {
  enabled: boolean; // ativa/desativa modo QA
  skus: string[]; // lista de SKUs alvo; vazio = aplica em todos os itens elegíveis
  forcePercentual?: number; // força percentual_flex (ex: 5 -> "5.00")
  missingIntegration?: boolean; // remove o campo integration_price_flex (simula “sem integral price”)
};

declare global {
  interface Window {
    qaConfigCheckout?: QaConfigCheckout;
  }
}

export const useCheckoutPage = () => {
  const { tables } = useTables();
  const navigate = useNavigate();
  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);
  const { updateTable } = useLoader();
  const { axiosCtx } = useFetchAxios();
  const { calculatedList } = usePrices();
  const { handlePrices } = useCalcPrice();
  const { handleUpdateStock, stockLeft } = useStock();
  const { cartTotals, listaTributacao } = useCartTotals();
  const { discountAmountClient, estimatedSaldoFlex, estimatedSaldoFlexClient } = useDiscountAmountFlex();
  const { connectivityStatus } = useNetworkStatusContext();
  const { cartDetails, handleRemoveItems, getLimit } = useCart();
  const { setRcaSaldoFlex: setFlex, rcaSaldoFlex } = useUserContext();
  const { clearClientDiscount, getDiscountProduct } = useDiscountFlex();
  const { version, paymentCode, setPaymentCode, setCanAccessSuccess, setOrderData, setCartTotals, setOrderItems } = useGlobals();
  const { selectedClientAddress, selectedClient, setSelectedClient, calculateDeliveryDate, setJanelaEntregaData } = useClient();

  // 🔧 Configuração de QA simples e global (o QA pode alterar via console)
  window.qaConfigCheckout = window.qaConfigCheckout || {
    enabled: false,
    skus: [],
    forcePercentual: 5,
    missingIntegration: false,
  };
  const qaConfig = window.qaConfigCheckout;

  const getStoredOrderNumber = useCallback((client: any) => {
    if (!client?.COD_CLIENTE) return null;
    const storedOrders = getOnStorage('order_numbers') || {};
    return storedOrders[client.COD_CLIENTE]?.orderNumber || null;
  }, []);

  const [, setSucess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erpNumber, setErpNumber] = useState('');

  const creditLimit = selectedClient?.SALDO_DISPONIVEL;

  // Normaliza qualquer valor numérico vindo como string (ex.: "1.234,56", "R$ 123,45") ou number
  const toNumber = useCallback((v: unknown): number => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    if (typeof v !== 'string') return 0;
    const s = v.trim();
    if (!s) return 0;
    // Remove símbolo de moeda e espaços
    let cleaned = s.replace(/\s|R\$|\u00A0/g, '');
    // Se contiver vírgula e não contiver ponto, assuma vírgula como separador decimal
    if (/,/.test(cleaned) && !/\./.test(cleaned)) {
      cleaned = cleaned.replace(/\./g, ''); // remove milhares por segurança
      cleaned = cleaned.replace(/,/g, '.');
    } else {
      // Caso tenha ambos, assume o último como separador decimal
      const lastComma = cleaned.lastIndexOf(',');
      const lastDot = cleaned.lastIndexOf('.');
      if (lastComma > lastDot) {
        // vírgula como decimal: remove pontos de milhar e troca vírgula por ponto
        cleaned = cleaned.replace(/\./g, '').replace(/,/g, '.');
      }
    }
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const listPaymentCode = useMemo(() => {
    return selectedClient?.COD_PAGAMENTO || [];
  }, [selectedClient?.COD_PAGAMENTO]);

  const productList = useMemo(() => {
    const codLoja = selectedClient?.COD_LOJA;
    const tabVenda = selectedClient?.COD_TABELA_VENDA;
    const condicoes = selectedClient?.COD_PAGAMENTO?.map((i) => i.cod_condicao) ?? [];

    const catalog = tables.current.dicionario_produtos;
    const listSubstTrib = tables.current.cod_regime_tributario;

    const parametroPrecoBase = normalizeParametroPrecoBase(
      tables.current.parametro_preco_base,
      codLoja,
      tabVenda,
      condicoes
    );

    const percentualfornecedor = tables.current.percentual_fornecedor ?? [];
    const percentualFornecedor = percentualfornecedor.filter((p) => `${p.NROTABVENDA}` === `${tabVenda}`);

    const parametroPrecoPromocao = tables.current.precoPromocaoCliente;

    const items = cartDetails?.[selectedClient?.COD_CLIENTE]?.items?.map((item) => {
      const product = catalog?.[item?.sku];
      if (!product) return null;
      const keyCalculated = `${selectedClient.COD_CLIENTE}-${product.COD_PRODUTO}`;
      const productWithPrice = {
        ...product,
        PRECO:
          calculatedList.current[keyCalculated] ||
          handlePrices(
            condicoes,
            product,
            parametroPrecoBase,
            percentualFornecedor,
            listSubstTrib,
            parametroPrecoPromocao,
            'checkout'
          ),
      };
      return { item, product: productWithPrice };
    });

    type ItemEntry = { item: CartItems; product: ProductProps };
    return (items?.filter((entry): entry is ItemEntry => entry != null) ?? []) as CartProductItem[];
  }, [cartDetails, handlePrices, selectedClient, tables, calculatedList]);

  const conditions = useMemo(() => listPaymentCode || null, [listPaymentCode]);

  const shippingData = useMemo(() => {
    if (selectedClientAddress && shippingMethod) {
      const deliveryDate = calculateDeliveryDate();
      if (deliveryDate) setJanelaEntregaData(deliveryDate);
      return [
        { dataType: 'address', ...selectedClientAddress },
        { dataType: 'shipping', deliveryDate, ...shippingMethod },
      ];
    }
    return [];
  }, [selectedClientAddress, calculateDeliveryDate, setJanelaEntregaData]);

  const cartItems = useMemo(() => cartItemsMock || null, []);
  const erpOrderNumber = useMemo(() => erpNumber || null, [erpNumber]);

  const generateOrderPayload = useCallback(() => {
    if (!productList || !selectedClient || !cartDetails || !paymentCode) {
      return null;
    }

    const items = productList.map((entry: CartProductItem) => {
      const [pack, qtd] = entry?.item?.option_uid?.split('_');
      const qty = Number(qtd);

      const clientCodSegmentos = selectedClient?.COD_SEGMENTO?.map((s) => s.cod_segmento) ?? [];

      const rawPrecos: any = entry?.product?.PRECO || {};
      const list: any[] = Array.isArray(rawPrecos) ? rawPrecos : Object.values(rawPrecos || {});
      const listByLoja = list.filter((i) => i.COD_LOJA === selectedClient?.COD_LOJA);

      const preco =
        findDirectPriceRecord({
          prices: listByLoja,
          pack: pack as any,
          packQty: qty,
          paymentCode,
          selectedClient,
        }) ?? ({} as any);

      const matchesOptionUid = (e: PackagingProductProps) =>
        pack === 'PK'
          ? e.SG_EMBALAGEM === pack && e.QTD_MULTIPLO_VENDA === qty
          : e.SG_EMBALAGEM === pack && e.QTD_EMBALAGEM === qty;

      const candidatosEmbalagem = (entry?.product?.EMBALAGEM ?? []).filter(
        (e) => clientCodSegmentos.includes(e.COD_SEGMENTO) && matchesOptionUid(e)
      );

      let embalagemDaLinha: PackagingProductProps | undefined;
      if (candidatosEmbalagem.length === 1) {
        embalagemDaLinha = candidatosEmbalagem[0];
      } else if (candidatosEmbalagem.length > 1) {
        const peloPreco = candidatosEmbalagem.find(
          (e) => preco?.NRO_SEGMENTO != null && Number(e.COD_SEGMENTO) === Number(preco.NRO_SEGMENTO)
        );
        embalagemDaLinha =
          peloPreco ??
          [...candidatosEmbalagem].sort(
            (a, b) =>
              clientCodSegmentos.indexOf(a.COD_SEGMENTO) - clientCodSegmentos.indexOf(b.COD_SEGMENTO)
          )[0];
      }

      let cod_segmento: number | undefined = embalagemDaLinha?.COD_SEGMENTO;
      if (cod_segmento == null && preco?.NRO_SEGMENTO != null) {
        const n = Number(preco.NRO_SEGMENTO);
        if (isNroSegmentoPrecoCuringa(n) || clientCodSegmentos.includes(n)) {
          cod_segmento = n;
        }
      }
      if (cod_segmento == null) {
        cod_segmento = selectedClient?.COD_SEGMENTO?.[0]?.cod_segmento;
      }

      // preços base/integration
      const integrationPrice = Number(preco?.VLR_PRECO_CALCULADO) / (Number(preco?.QTD_MULTIPLO_VENDA) || 1);

      // dados de flex/discount
      const flexIntegrationPrice = getDiscountProduct({
        productId: entry?.product?.COD_PRODUTO || 0,
        package: pack,
        quantity: qty,
      });

      const percentualFlexStr = flexIntegrationPrice
        ? (Number(flexIntegrationPrice?.percentual).toFixed(2) || '0').toString()
        : '0';

      const valorAjuste = (Number(percentualFlexStr) / 100) * integrationPrice;
      let integrationPriceFlexNum = Number(((integrationPrice + valorAjuste) * 100) / 100);

      const priceFlex = flexIntegrationPrice?.discountPrice?.[paymentCode] || 0;
      const totalPriceFlex = priceFlex * entry.item.quantity;
      const unitPriceFlexPerItem = (priceFlex / qty).toFixed(2);

      const precoPauta = preco?.VLR_PAUTA || 0;

      const tributacao = preco
        ? HandlePrices.getTributacao(selectedClient, preco.COD_TRIBUTACAO_PRODUTO, listaTributacao)
        : undefined;

      const precoFlexST = HandlePrices.CalculaST(tributacao, integrationPriceFlexNum * entry.item.quantity, precoPauta);

      let percentualFlexOut = percentualFlexStr;
      const hasFlex = Number(percentualFlexOut) !== 0 || !!flexIntegrationPrice?.percentual;

      // 🔹 Simulação QA: aplica apenas se enabled=true e o item está na lista (ou se lista vazia = aplica em todos)
      const isTargetSku = qaConfig.enabled && (!qaConfig.skus?.length || qaConfig.skus.includes(String(entry?.item?.sku)));
      if (isTargetSku && hasFlex) {
        // força integration_price_flex = 0 (para disparar sua regra atual)
        integrationPriceFlexNum = 0;

        // opcionalmente remove o campo integration_price_flex (simula "sem integral price" no payload)
        // (remoção é feita na montagem do objeto final, ver abaixo)
        // força percentual se configurado
        if (typeof qaConfig.forcePercentual === 'number' && !Number.isNaN(qaConfig.forcePercentual)) {
          percentualFlexOut = qaConfig.forcePercentual.toFixed(2);
        } else if (percentualFlexOut === '0') {
          // garante que o caso de teste aconteça (se o produto não tinha percentual)
          percentualFlexOut = '5.00';
        }
      }

      // monta o item do payload
      const baseItem = {
        cod_segmento,
        cod_produto: entry?.product?.COD_PRODUTO,
        unilever: entry?.product?.UNILEVER,
        sku: entry?.item?.sku.toString(),
        product: entry?.product?.DESC_PRODUTO || '',
        price: preco?.VLR_FINAL ? (preco?.VLR_FINAL * entry?.item?.quantity)?.toFixed(2)?.toString() : '',
        unit_price: preco?.VLR_FINAL?.toString() || '',
        unit_price_per_item: preco?.VLR_FINAL ? (preco.VLR_FINAL / qty)?.toFixed(2)?.toString() : '',
        st_price: preco?.ST_VLR_FINAL?.toString() || '',
        integration_price: integrationPrice,
        quantity: entry?.item?.quantity || 0,
        packaging: entry?.item?.option_uid || '',
        st_price_packaging: preco?.ST_VLR_FINAL || 0,

        // FLEX
        unit_price_flex: hasFlex ? priceFlex.toFixed(2) : '0',
        // integration_price_flex será ajustado abaixo para suportar remoção do campo no caso QA
        integration_price_flex: hasFlex ? integrationPriceFlexNum.toFixed(2) : '0',
        unit_price_per_item_flex: hasFlex ? unitPriceFlexPerItem : '0',
        st_price_flex: hasFlex ? precoFlexST : 0,
        price_flex: hasFlex ? totalPriceFlex.toFixed(2) : '0',
        percentual_flex: Number(percentualFlexOut) !== 0 ? percentualFlexOut : '0',

        quantity_multiplo: preco?.QTD_MULTIPLO_VENDA || 1,
        status: '',
        reason: '',
      } as any;

      // Se QA pediu para "remover" o campo integration_price_flex, deletamos do objeto final:
      if (isTargetSku && hasFlex && qaConfig.enabled && qaConfig.missingIntegration) {
        delete baseItem.integration_price_flex; // @ts-ignore
      }

      return baseItem;
    });

    const storedOrderNumber = getStoredOrderNumber(selectedClient);
    const cartOpenedAt = getOnStorage('cart_opened_at');

    if (selectedClient && items && cartDetails && storedOrderNumber) {
      return {
        version,
        origin_function: 'process',
        current_time: format(new Date(), 'yyyy-MM-dd HH:mm:ss', { locale: ptBR }),
        cart_opened_at: cartOpenedAt || '',
        company: {
          unilever: selectedClient.UNILEVER,
          name: selectedClient.NM_CLIENTE.toString(),
          id: selectedClient.COD_CLIENTE.toString(),
          cnpj: selectedClient.NR_DOCUMENTO.toString(),
          Cod_rca: selectedClient?.COD_RCA_CLIENTE?.toString() || selectedClient.COD_RCA.toString(),
        },
        orders: {
          order_number: storedOrderNumber,
          erp_order_number: erpNumber ?? '',
          order_number_magento: '',
          origin: 'BateForte_web_mobile_rca',
          items: cartDetails?.[selectedClient?.COD_CLIENTE]?.items?.length,
          status: '',
          reason: '',
          cod_loja: Number(selectedClient.COD_LOJA || 0),
          order_date: format(new Date(), 'EEEE, dd/MM/yyyy', { locale: ptBR }),
          total: Number(cartTotals?.total?.toFixed(2)),
          payment_terms: paymentCode.toString(),
          actions_pending: items,
        },
      } as OrderPayloadProps;
    }
    return null;
  }, [
    productList,
    selectedClient,
    cartDetails,
    paymentCode,
    listaTributacao,
    getDiscountProduct,
    cartTotals?.total,
    erpNumber,
    version,
    getStoredOrderNumber,
    qaConfig, // mantém referência (QA pode trocar via console e dar refresh)
  ]);

  const orderPayload = useMemo(() => generateOrderPayload(), [generateOrderPayload]);

  console.log('Order Payload:', orderPayload);

  const total = useMemo(() => cartTotals?.total, [cartTotals?.total]);

  // Garante números válidos para o cálculo de crédito disponível
  const normalized = useMemo(() => {
    return {
      creditLimit: toNumber(creditLimit as any),
      total: toNumber(cartTotals?.total as any),
    };
  }, [creditLimit, cartTotals?.total, toNumber]);

  // Usamos o valor "bruto" para comparação (evita o caso de -0.00 após arredondar)
  const rawAvailableCredit = useMemo(() => normalized.creditLimit - normalized.total, [normalized]);

  const availableCredit = useMemo(() => Number(rawAvailableCredit.toFixed(2)), [rawAvailableCredit]);

  // Excedeu o limite se for realmente negativo OU se arredondou para -0 (Object.is detecta -0)
  const creditLimitExceeded = useMemo(
    () => rawAvailableCredit < 0 || Object.is(availableCredit, -0),
    [rawAvailableCredit, availableCredit]
  );

  console.log('Checkout - credit calc:', {
    creditLimitRaw: creditLimit,
    totalRaw: cartTotals?.total,
    creditLimit: normalized.creditLimit,
    total: normalized.total,
    availableCredit,
    creditLimitExceeded,
  });

  const projectedSaldoFlex = estimatedSaldoFlex;
  const checkoutSaldoNegativo = projectedSaldoFlex < 0;

  const hasDiscountApplied = useMemo(() => Number(discountAmountClient) < 0, [discountAmountClient]);
  const blockByCredit = useMemo(
    // creditLimitExceeded já cobre o caso; mantemos estrutura para fácil extensão futura
    () => creditLimitExceeded,
    [creditLimitExceeded]
  );

  const disabled = useMemo(() => {
    return (
      cartDetails?.[selectedClient?.COD_CLIENTE]?.items?.length === 0 ||
      loading ||
      !selectedClient ||
      blockByCredit ||
      (checkoutSaldoNegativo && hasDiscountApplied)
    );
  }, [cartDetails, selectedClient, blockByCredit, loading, checkoutSaldoNegativo]);

  const clearOrderNumber = useCallback(() => {
    if (!selectedClient?.COD_CLIENTE) return;
    const storedOrders = getOnStorage('order_numbers') || {};
    if (storedOrders[selectedClient.COD_CLIENTE]) {
      delete storedOrders[selectedClient.COD_CLIENTE];
      setOnStorage('order_numbers', storedOrders);
    }
  }, [selectedClient?.COD_CLIENTE]);

  const handleControlStock = useCallback(() => {
    productList?.forEach((entry) => {
      const [_, qtd] = entry?.item?.option_uid?.split('_');
      const fullStock = getLimit(entry?.item?.sku);
      const hasRegistered = stockLeft?.[entry?.item?.sku] as StockProps;
      const stock = stockLeft?.[entry?.item?.sku]?.stock_left as number;

      handleUpdateStock({
        product_code: entry?.item?.sku,
        stock: {
          stock_left:
            hasRegistered && stock === entry?.item?.quantity * Number(qtd)
              ? 0
              : hasRegistered && stock > 0
                ? stock - entry?.item?.quantity * Number(qtd)
                : !hasRegistered && fullStock === entry?.item?.quantity * Number(qtd)
                  ? 0
                  : fullStock - entry?.item?.quantity * Number(qtd),
        },
      });
    });
  }, [productList, getLimit, stockLeft, handleUpdateStock]);

  const storeOrderData = useCallback(
    (payloadToStore: OrderPayloadProps | null) => {
      try {
        if (!payloadToStore) return false;
        setOrderData(payloadToStore);
        setCartTotals(cartTotals);
        setOrderItems(productList || []);
        return true;
      } catch (error) {
        console.error('Erro ao salvar os dados no DB:', error);
        return false;
      }
    },
    [productList, setOrderData, setCartTotals, setOrderItems, cartTotals]
  );

  const checkoutOrder = useCallback(async () => {
    setLoading(true);
    // Segurança adicional: mesmo que o botão esteja desabilitado na UI,
    // garantimos aqui que pedidos que estouram o limite não avancem
    if (blockByCredit) {
      setLoading(false);
      window.alert('Não foi possível finalizar: o total do pedido excede o limite de crédito disponível.');
      return;
    }
    let currentPayload = generateOrderPayload();

    // ————— Helpers de validação —————
    const isMissing = (v: any) =>
      v === null ||
      v === undefined ||
      v === '' ||
      (typeof v === 'number' && !Number.isFinite(v)) ||
      (typeof v === 'string' && v.trim().toLowerCase() === 'nan');

    const missingFieldsBase = (it: any) => {
      const miss: string[] = [];
      if (isMissing(it.price)) miss.push('price');
      if (isMissing(it.unit_price)) miss.push('unit_price');
      if (isMissing(it.unit_price_per_item)) miss.push('unit_price_per_item');
      if (isMissing(it.integration_price)) miss.push('integration_price');
      return miss;
    };

    const missingFieldsFlex = (it: any) => {
      const miss: string[] = [];
      const hasPercentual = !!it.percentual_flex && `${it.percentual_flex}` !== '0';
      if (!hasPercentual) return miss;

      // Campos de flex que precisam existir
      if (isMissing(it.unit_price_flex)) miss.push('unit_price_flex');
      if (isMissing(it.unit_price_per_item_flex)) miss.push('unit_price_per_item_flex');
      if (isMissing(it.price_flex)) miss.push('price_flex');

      // integration_price_flex deve existir e ser > 0
      const ipf = it.integration_price_flex;
      const ipfNum = Number(ipf);
      if (ipf === undefined || ipf === null || ipf === '' || !Number.isFinite(ipfNum) || ipfNum <= 0) {
        miss.push('integration_price_flex');
      }

      // st_price_flex pode ser 0; só consideramos ausente se vier undefined/null/NaN/vazio
      // mas se tiver st_price base, ele se torna obrigatório
      const hasBaseSt = !isMissing(it.st_price) && Number(it.st_price) > 0;
      if (hasBaseSt && isMissing(it.st_price_flex)) {
        miss.push('st_price_flex');
      }

      return miss;
    };

    // ————— Validação dos itens —————
    const validateItems = (payload: OrderPayloadProps | null) => {
      const items = payload?.orders?.actions_pending || [];
      return (items as any[])
        .map((it) => {
          const baseMiss = missingFieldsBase(it);
          const flexMiss = missingFieldsFlex(it);
          return {
            sku: it.sku,
            product: it.product,
            missing: [...baseMiss, ...flexMiss],
            isFlexOnly: baseMiss.length === 0 && flexMiss.length > 0,
          };
        })
        .filter((p) => p.missing.length > 0);
    };

    let problems = validateItems(currentPayload);
    const hasFlexProblems = problems.some((p) => p.isFlexOnly);

    // Se houver apenas problemas de flex, tenta recalcular uma vez
    if (problems.length > 0 && hasFlexProblems) {
      console.log('Problemas de flex detectados. Tentando recalcular preços...');
      currentPayload = generateOrderPayload(); // Força o recálculo
      problems = validateItems(currentPayload); // Revalida
    }

    if (problems.length > 0) {
      const details = problems.map((p) => `\n- ${p.product} (SKU: ${p.sku}) -> faltando: ${p.missing.join(', ')}`).join('');

      window.alert(
        'Há itens com campos de preço obrigatórios ausentes/inválidos. ' +
          'Revise antes de finalizar:' +
          details +
          '\n\nDica: remova e readicione o item no carrinho.'
      );
      setLoading(false);
      return;
    }

    // ————— Continua o fluxo normal —————
    const finalPayload = currentPayload;
    const storageSuccess = storeOrderData(finalPayload);

    if (!storageSuccess) {
      window.alert('Erro ao armazenar os dados do pedido. O checkout não pode ser finalizado.');
      setLoading(false);
      return;
    }

    const addOrderToStorage = async (payload: OrderPayloadProps) => {
      let offlineOrders = (getOnStorage('offline_orders') as any[]) || [];
      offlineOrders.push(payload);
      setOnStorage('offline_orders', offlineOrders);
      clearOrderNumber();
    };

    const updateStorages = async () => {
      const atualEstoque = tables.current.estoque;
      const newEstoque =
        finalPayload?.orders.actions_pending?.reduce(
          (acc, item: any) => {
            return {
              ...acc,
              [item.sku]: item.quantity * (item.quantity_multiplo || 1) || 0,
            };
          },
          {} as Record<number, number>
        ) || {};

      const updatedEstoque = atualEstoque.map((etq: any) => {
        return {
          ...etq,
          QTD_ESTOQUE: etq.QTD_ESTOQUE - (newEstoque?.[etq.COD_PRODUTO] || 0),
        };
      });
      await updateTable(updatedEstoque, 'estoque');

      // Atualiza SALDO_DISPONIVEL do cliente também no fluxo offline
      // para respeitar o limite em pedidos subsequentes enquanto estiver offline
      if (selectedClient) {
        const client = { ...selectedClient } as any;
        client.SALDO_DISPONIVEL = Number(((client.SALDO_DISPONIVEL || 0) - (cartTotals.total as number)).toFixed(2));
        setOnStorage('selected_client', client);
        setSelectedClient(client);

        const listClients = tables.current.cliente;
        const updatedList = listClients?.map((i: any) => (i.COD_CLIENTE === selectedClient?.COD_CLIENTE ? client : i));
        await updateTable(updatedList, 'cliente');
      }
    };

    console.log(`finalPayload`, finalPayload);

    if (connectivityStatus === 'online') {
      try {
        const response = (await axiosCtx
          .post('/integra-pedido', finalPayload)
          .then((res) => res)
          .catch((error) => {
            throw (
              error?.body?.message ||
              error?.response?.data?.message ||
              error?.message ||
              error?.data?.message ||
              error?.body ||
              error?.data ||
              error
            );
          })) as any;

        if (response?.data?.message) {
          const synchedList = getOnStorage('synched_orders') || [];
          synchedList.push({
            ...finalPayload,
            orders: {
              ...finalPayload?.orders,
              status: 'processando',
              erp_order_number: '',
              erp_order_number_client: erpOrderNumber ?? '',
            },
          });

          const skus = finalPayload?.orders?.actions_pending?.map((i) => Number(i.sku)) || [];
          setOnStorage('synched_orders', synchedList);

          clearClientDiscount();

          //novo saldo flex

          if (setFlex) setFlex((rcaSaldoFlex || 0) + discountAmountClient);

          const client = { ...selectedClient } as any;

          // calculo de preco para saldo ERP do usuario
          client.SALDO_DISPONIVEL = Number(((client.SALDO_DISPONIVEL || 0) - (cartTotals.total as number)).toFixed(2));

          setOnStorage('selected_client', client);
          setSelectedClient(client);

          const listClients = tables.current.cliente;
          const updatedList = listClients?.map((i: any) => (i.COD_CLIENTE === selectedClient?.COD_CLIENTE ? client : i));
          await updateTable(updatedList, 'cliente');

          const atualEstoque = tables.current.estoque;
          const newEstoque =
            finalPayload?.orders.actions_pending?.reduce(
              (acc, item: any) => {
                const qt = item.packaging.split('_')[1];
                return {
                  ...acc,
                  [`${String(item.cod_produto as any)}`]:
                    item.quantity * (item.quantity_multiplo > 1 ? item.quantity_multiplo : Number(qt)) || 0,
                };
              },
              {} as Record<number, number>
            ) || {};

          const updatedEstoque = atualEstoque.map((etq: any) => {
            return {
              ...etq,
              QTD_ESTOQUE: etq.QTD_ESTOQUE - (newEstoque?.[Number(etq.COD_PRODUTO)] || 0),
            };
          });
          await updateTable(updatedEstoque, 'estoque');

          setSucess(true);
          clearOrderNumber();
          handleControlStock();
          handleRemoveItems();
          setCanAccessSuccess(true);
          navigate(`/sucesso`);
        } else {
          setLoading(false);
          setSucess(false);
          await registerEvent({
            tipoEvento: 'CHECKOUT_ERROR',
            obsEvento: `Um erro ocorreu ao fechar pedido: ${response?.data?.message}`,
            stEvento: 0,
          });
          
          if (finalPayload) {
            addOrderToStorage(finalPayload);
            await updateStorages();
            clearClientDiscount();
            if (setFlex) setFlex((rcaSaldoFlex || 0) + discountAmountClient);
            setLoading(false);
            setSucess(true);
            handleControlStock();
            handleRemoveItems();
            setCanAccessSuccess(true);
            navigate(`/sucesso?st=err`);
          }
        }
      } catch (error) {
        setLoading(false);
        setSucess(false);
        
        await registerEvent({
          tipoEvento: 'CHECKOUT_ERROR',
          obsEvento: `Um erro ocorreu ao fechar pedido. Tente novamente mais tarde: ${error}`,
          stEvento: 0,
        });
        
        if (finalPayload) {
          addOrderToStorage(finalPayload);
          await updateStorages();
          clearClientDiscount();
          if (setFlex) setFlex((rcaSaldoFlex || 0) + discountAmountClient);
          setLoading(false);
          setSucess(true);
          handleControlStock();
          handleRemoveItems();
          setCanAccessSuccess(true);
          navigate(`/sucesso?st=err`);
        }
      }
    } else {
      try {
        if (finalPayload) {
          await addOrderToStorage(finalPayload);
          await updateStorages();
          clearClientDiscount();
          if (setFlex) setFlex((rcaSaldoFlex || 0) + discountAmountClient);
        } else {
          console.error('Order payload is null');
        }

        setLoading(false);
        setSucess(true);
        handleControlStock();
        handleRemoveItems();
        setCanAccessSuccess(true);
        navigate(`/sucesso`);
      } catch (e) {
        setLoading(false);
        setSucess(false);
        console.error('Error sending order:', e);
      }
    }
  }, [
    generateOrderPayload,
    storeOrderData,
    connectivityStatus,
    axiosCtx,
    erpNumber,
    selectedClient,
    clearClientDiscount,
    setFlex,
    rcaSaldoFlex,
    discountAmountClient,
    cartTotals,
    setSelectedClient,
    tables,
    updateTable,
    clearOrderNumber,
    handleControlStock,
    handleRemoveItems,
    setCanAccessSuccess,
    navigate,
    getDiscountProduct,
  ]);

  return {
    total,
    disabled,
    cartItems,
    conditions,
    productList,
    paymentCode,
    shippingData,
    checkoutOrder,
    setPaymentCode,
    creditLimitExceeded,
    erpNumber,
    setErpNumber,
  };
};
